// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { WebsiteElementData } from '@way/theme-host/shared/components/website-element';
import {
  DarkPageHeaderComponent as PageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { DarkPageFooterComponent as PageFooterComponent } from '../shared/footer/footer.component';
import { HeroLayout1PreviewComponent } from '../../panels/hero-panel/layouts/layout_1';
import { HeroLayout2PreviewComponent } from '../../panels/hero-panel/layouts/layout_2';
import { HeroLayout3PreviewComponent } from '../../panels/hero-panel/layouts/layout_3';
import { HeroLayout4PreviewComponent } from '../../panels/hero-panel/layouts/layout_4';
import { HeroLayout5PreviewComponent } from '../../panels/hero-panel/layouts/layout_5';
import { HeroLayout6PreviewComponent } from '../../panels/hero-panel/layouts/layout_6';
import {
  InfoLayout1PreviewComponent,
  InfoLayout1Data,
} from '../../panels/info-panel/layouts/layout_1';
import {
  InfoLayout2PreviewComponent,
  InfoLayout2Data,
} from '../../panels/info-panel/layouts/layout_2';
import {
  InfoLayout3PreviewComponent,
  InfoLayout3Data,
} from '../../panels/info-panel/layouts/layout_3';
import {
  InfoLayout4PreviewComponent,
  InfoLayout4Data,
} from '../../panels/info-panel/layouts/layout_4';
import {
  InfoLayout5PreviewComponent,
  InfoLayout5Data,
} from '../../panels/info-panel/layouts/layout_5';
import {
  BookingLayout1PreviewComponent,
  BookingLayout1Data,
} from '../../panels/booking-panel/layouts/layout_1';
import {
  BookingLayout2PreviewComponent,
  BookingLayout2Data,
} from '../../panels/booking-panel/layouts/layout_2';
import {
  ContactLayout2PreviewComponent,
  ContactLayout2Data,
} from '../../panels/contact-panel/layouts/layout_2';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface WebsiteElement extends Omit<WebsiteElementData, 'selected'> {
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'packages'
    | 'coupons'
    | 'fleets'
    | 'express-details';
  selected?: boolean; // Make selected optional for compatibility
  content: {
    title?: string;
    titleStyle?:
      | string
      | {
          fontWeight?: 'bold' | null;
          fontStyle?: 'italic' | null;
          textDecoration?: 'underline' | null;
          textAlign?: 'left' | 'center' | 'right' | 'justify' | null;
        }; // Text color/style from title.style.text_style (string) or object (minimal theme compatibility)
    backgroundColor?: string; // Background color from layout.background_color
    subtitle?: string;
    description?: string;
    buttonText?: string;
    buttonLink?: string; // Button navigation link
    button?: {
      text?: string;
      backgroundColor?: string;
      textColor?: string;
    };
    imageUrl?: string;
    image?: {
      src?: string;
      alt?: string;
      width?: string;
      height?: string;
      scaletype?: 'fill' | 'fit' | 'stretch';
    };
    backgroundImage?: string; // For full background images (e.g., hero section)
    layout?:
      | 'layout_1'
      | 'layout_2'
      | 'layout_3'
      | 'layout_4'
      | 'layout_5'
      | 'layout_6'
      | 'layout_7'
      | '1'
      | '2'
      | '3'
      | '4'
      | '5'
      | '6'
      | '7'
      | {
          type:
            | 'layout_1'
            | 'layout_2'
            | 'layout_3'
            | 'layout_4'
            | 'layout_5'
            | 'layout_6'
            | 'layout_7';
          grid_columns?: number;
          image_alignment?: string;
          padding?: string;
          background_color?: string;
        }; // Hero layout type - supports both old format (string) and new API format (object with type)
    testimonials?: {
      id: string;
      text: string;
      author: {
        name?: string;
        title?: string;
        avatar?: string;
      };
      rating?: number;
      date?: string;
    }[];
    services?: {
      id?: string;
      name?: string;
      title?: string;
      description?: string;
      price?: string;
      duration?: string;
      image?: string;
      icon?: string;
    }[];
    formFields?: {
      id: string;
      name: string;
      label: string;
      type: string;
      placeholder?: string;
      required: boolean;
      validation?: any;
      options?: {
        value: string;
        label: string;
        disabled?: boolean;
      }[];
    }[];
    availableServices?: {
      id: string;
      name: string;
      description: string;
      price: string;
      duration: string;
      available: boolean;
    }[];
    map?: {
      image: string;
      alt: string;
      coordinates?: {
        lat: number;
        lng: number;
      };
      pin?: {
        image: string;
        alt: string;
      };
    };
    address?: string;
    phone?: string;
    email?: string;
    mapEmbedUrl?: string;
    contactInfo?: {
      address?: {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country?: string;
      };
      phone?: string;
      email?: string;
      website?: string;
    };
    socialLinks?: {
      platform: string;
      url?: string;
      link?: string;
      icon?: string;
      label?: string;
    }[];
    businessHours?: {
      day: string;
      openTime: string;
      closeTime: string;
      closed: boolean;
    }[];
    logo?: {
      image: string;
      alt: string;
      link: string;
    };
    navigation?: {
      id: string;
      label: string;
      link: string;
      active: boolean;
    }[];
    ctaButton?: {
      text: string;
      link: string;
      style: string;
    };
    mobileMenu?: boolean;
  };
}

@Component({
  selector: 'app-dark-home-page-preview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageHeaderComponent,
    PageFooterComponent,
    HeroLayout1PreviewComponent,
    HeroLayout2PreviewComponent,
    HeroLayout3PreviewComponent,
    HeroLayout4PreviewComponent,
    HeroLayout5PreviewComponent,
    HeroLayout6PreviewComponent,
    InfoLayout1PreviewComponent,
    InfoLayout2PreviewComponent,
    InfoLayout3PreviewComponent,
    InfoLayout4PreviewComponent,
    InfoLayout5PreviewComponent,
    BookingLayout1PreviewComponent,
    BookingLayout2PreviewComponent,
    ContactLayout2PreviewComponent,
  ],
  templateUrl: './home-page-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./home-page-preview.component.scss'],
})
export class DarkHomePagePreviewComponent implements OnChanges, OnInit, OnDestroy {
  @Input() elements: WebsiteElement[] = [];
  @Input() sectionVisibility: Record<string, boolean> = {};
  @Input() pages: any[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Input() pageData: any = null; // Page data to access json_data.website.header and json_data.website.footer
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  // Cache for map embed URL to prevent flickering
  private cachedMapEmbedUrl: SafeResourceUrl | null = null;
  private cachedMapEmbedUrlSource: string | null = null;

  // Testimonials carousel state
  currentTestimonialSlide = 0;
  private autoSlideInterval: any = null;
  private readonly AUTO_SLIDE_INTERVAL = 5000; // 5 seconds
  private touchStartX = 0;
  private touchEndX = 0;

  constructor(
    private logger: LoggerService,
    private sanitizer: DomSanitizer
  ) {}

  // Element configuration for reusability
  readonly ELEMENT_CONFIG = {
    header: { class: 'header-element', index: 0 },
    hero: { class: 'hero-element', index: 1 },
    welcome: { class: 'section-element', index: 2 },
    testimonials: { class: 'testimonials-element', index: 3 },
    services: { class: 'services-element', index: 4 },
    booking: { class: 'booking-element', index: 5 },
    contact: { class: 'contact-element', index: 6 },
    footer: { class: 'footer-element', index: 7 },
  };

  /**
   * Handle Angular SimpleChanges for the component: initialize elements, clear map cache, and manage testimonial auto-slide.
   * @example
   * gOnChanges({ elements: { currentValue: [], previousValue: [] } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Angular change object mapping input property names to their SimpleChange values.
   * @returns {{void}} Void; updates component state (initialization, cache reset, and auto-slide control) with no return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['elements'] && this.elements.length === 0) {
      this.initializeElements();
    }

    // Clear map embed URL cache when elements change to recalculate
    if (changes['elements']) {
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
    }

    // Restart auto-slide if testimonials change
    if (changes['elements']) {
      const testimonials = this.getTestimonials();
      if (testimonials.length > 0 && this.getTotalTestimonialSlides() > 1) {
        this.currentTestimonialSlide = 0;
        this.startAutoSlide();
      } else {
        this.stopAutoSlide();
      }
    }

    // Note: Footer links are computed directly in template via getFooterQuickLinks()
    // which will automatically update when pages array changes due to Angular change detection
    // Header navigation comes from elements[0].content.navigation which is updated by
    // populateHeaderFromTemplate() in the parent component
  }

  private initializeElements(): void {
    this.elements = Object.keys(this.ELEMENT_CONFIG).map((key, index) => ({
      id: key,
      type: key as WebsiteElement['type'],
      content: {},
      selected: false,
    }));
  }

  onElementClick(element: WebsiteElement | undefined): void {
    if (!element) return;

    this.updateSelection(element);
    this.elementSelect.emit(element);
  }

  private updateSelection(selectedElement: WebsiteElement): void {
    this.elements.forEach(e => (e.selected = e.id === selectedElement.id));
  }

  getElementByIndex(index: number): WebsiteElement | undefined {
    return this.elements[index];
  }

  /**
   * Get title color from titleStyle (handles both string and object types)
   * Falls back to branding heading color if no titleStyle is set
   */
  getTitleColor(element: WebsiteElement | undefined): string | null {
    if (!element?.content?.titleStyle) {
      return this.getHeadingColor();
    }
    const titleStyle = element.content.titleStyle;
    // If it's a string, return it (it's a color)
    if (typeof titleStyle === 'string') {
      return titleStyle;
    }
    // If it's an object (from minimal theme), return null (objects don't contain color info)
    return null;
  }

  /**
   * Check if an element should be visible based on section visibility settings
   */
  isElementVisible(elementId: string): boolean {
    // First check if element exists in elements array
    const elementExists = this.elements?.some(
      el => el.id === elementId || (el as any).originalKey === elementId
    );
    if (!elementExists) {
      return false; // Hide if element doesn't exist
    }

    // If no visibility map provided, show all existing elements
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    // Check visibility for this element ID
    return this.sectionVisibility[elementId] !== false;
  }

  getElementClass(type: string): string {
    return this.ELEMENT_CONFIG[type as keyof typeof this.ELEMENT_CONFIG]?.class || '';
  }

  /**
   * Arrange form field objects into rows for rendering; textareas occupy their own full-width row, other fields are grouped in pairs.
   * @example
   * etFormRows([{type: 'text', name: 'first'}, {type: 'text', name: 'second'}, {type: 'textarea', name: 'desc'}])
   * [[{type: 'text', name: 'first'}, {type: 'text', name: 'second'}], [{type: 'textarea', name: 'desc'}]]
   * @param {{any[]}} {{formFields}} - Array of form field objects to arrange into rows.
   * @returns {{any[][]}} Returns a 2D array where each inner array is a row of fields (textareas as single-item rows, others in pairs).
   **/
  getFormRows(formFields: any[]): any[][] {
    if (!formFields || formFields.length === 0) return [];

    const rows: any[][] = [];
    let i = 0;

    while (i < formFields.length) {
      const field = formFields[i];

      // If it's a textarea, put it in its own row (full width)
      if (field.type === 'textarea') {
        rows.push([field]);
        i++;
      } else {
        // For other fields, group them in pairs
        const row = formFields.slice(i, i + 2);
        rows.push(row);
        i += row.length;
      }
    }

    return rows;
  }

  openDatePicker(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input && input.type === 'date') {
      this.triggerDatePicker(input);
    }
  }

  openDatePickerFromIcon(input: HTMLInputElement, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (input && input.type === 'date') {
      this.triggerDatePicker(input);
    }
  }

  /**
   * Temporarily removes readonly and opens the native date picker for the given input, using showPicker when available and falling back to focus+click, then restores readonly.
   * @example
   * triggerDatePicker(document.querySelector('#date-input'))
   * undefined
   * @param {HTMLInputElement} input - The date input element to open the native date picker for.
   * @returns {void} No return value.
   **/
  private triggerDatePicker(input: HTMLInputElement): void {
    // Remove readonly temporarily to allow native date picker to open
    input.removeAttribute('readonly');

    // Try modern showPicker API first
    if (input.showPicker) {
      try {
        const result = input.showPicker() as any;
        // Check if showPicker returns a Promise
        if (result && typeof result === 'object' && typeof result.catch === 'function') {
          result.catch(() => {
            // Fallback: focus and click if showPicker fails
            input.focus();
            input.click();
          });
        } else {
          // If showPicker returns void, just focus and click
          input.focus();
          input.click();
        }
      } catch (error) {
        // Fallback if showPicker throws
        input.focus();
        input.click();
      }
    } else {
      // Fallback for browsers without showPicker
      input.focus();
      input.click();
    }

    // Re-add readonly after a short delay to prevent manual typing
    setTimeout(() => {
      input.setAttribute('readonly', 'readonly');
    }, 200);
  }

  // Header data helpers for shared component
  /**
   * Resolve and return the header logo configuration from page data or the header element fallback, or null if none is found.
   * @example
   * etHeaderLogo()
   * { image: 'https://example.com/logo.png', text: 'Site Name', link: '/', alt: 'Site Logo' }
   * @param {{void}} {{none}} - No arguments.
   * @returns {{Logo|null}} Logo object with image, text, link and alt properties, or null if not available.
   **/
  getHeaderLogo(): Logo | null {
    // Priority 1: Check json_data.website.header.logo (header section logo)
    if (this.pageData?.json_data?.website?.header?.logo) {
      const headerLogo = this.pageData.json_data.website.header.logo;
      if (headerLogo.url || headerLogo.image) {
        return {
          image: headerLogo.url || headerLogo.image || '',
          text: headerLogo.text || 'Logo',
          link: headerLogo.link || '/',
          alt: headerLogo.alt || 'Logo',
        };
      }
      if (headerLogo.text) {
        return {
          image: '',
          text: headerLogo.text,
          link: headerLogo.link || '/',
          alt: headerLogo.alt || 'Logo',
        };
      }
    }

    // Priority 2: Check header element content (fallback)
    const headerElement = this.getElementByIndex(0);
    if (headerElement?.content?.logo) {
      const logoContent = headerElement.content.logo as any;
      return {
        image: logoContent.image || logoContent.url || '',
        alt: logoContent.alt || 'Logo',
        text: logoContent.text,
        link: logoContent.link || '/',
      };
    }
    return null;
  }

  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getElementByIndex(0);
    if (headerElement?.content?.navigation) {
      return headerElement.content.navigation.map((nav: any) => ({
        label: nav.label,
        link: nav.link,
        active: nav.active,
      }));
    }
    return [];
  }

  /**
   * Retrieve the header call-to-action (CTA) button from the first header element, returning a CTAButton with a text property (empty string if not present).
   * @example
   * etHeaderCTAButton()
   * { text: 'Get Started' }
   * @returns {{CTAButton | null}} Return the CTAButton object if available; otherwise returns a default CTAButton with an empty text property.
   **/
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getElementByIndex(0);
    const primaryColor = this.getPrimaryColor();
    if (headerElement?.content?.ctaButton) {
      const button = headerElement.content.ctaButton as any;
      return {
        text: button.text || '',
        link: button.link || '#booking',
        backgroundColor: button.backgroundColor || primaryColor || '#1B5BFF',
        textColor: button.textColor || '#FFFFFF',
      };
    }
    return {
      text: 'Book Wash',
      link: '#booking',
      backgroundColor: primaryColor || '#1B5BFF',
      textColor: '#FFFFFF',
    };
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getElementByIndex(0);
    if (headerElement?.content?.backgroundColor) {
      return headerElement.content.backgroundColor as string;
    }
    return null;
  }

  isHeaderSelected(): boolean {
    return this.getElementByIndex(0)?.selected || false;
  }

  onHeaderClick(): void {
    const headerElement = this.getElementByIndex(0);
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  /**
   * Determine and normalize the hero section layout identifier from the component's elements.
   * @example
   * etHeroLayout()
   * 'layout_1'
   * @param {{any}} {{this}} - The component instance used to access elements and their content.
   * @returns {{'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7'}} Return the normalized layout string for the hero section.
   **/
  getHeroLayout():
    'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7' {
    const heroElement = this.getElementByIndex(1);
    if (!heroElement) return 'layout_1';

    const layout = heroElement.content?.layout;
    if (!layout) return 'layout_1';

    // Handle API structure: layout is an object with type property
    if (typeof layout === 'object' && layout !== null && 'type' in layout) {
      const layoutType = (layout as any).type;
      if (typeof layoutType === 'string' && layoutType.startsWith('layout_')) {
        return layoutType as
          'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
      }
      // If type is just a number, convert to layout_X format
      if (typeof layoutType === 'string' && /^[1-7]$/.test(layoutType)) {
        return `layout_${layoutType}` as
          'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
      }
    }

    // Handle old format: layout is a string like '1', '2', etc.
    if (typeof layout === 'string') {
      if (layout.startsWith('layout_')) {
        return layout as
          'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
      }
      if (/^[1-7]$/.test(layout)) {
        return `layout_${layout}` as
          'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
      }
    }

    return 'layout_1';
  }

  /**
   * Get the background color for the hero element from the component's elements; prefers content.layout.background_color and falls back to content.backgroundColor, returning an empty string when not present.
   * @example
   * etHeroBackgroundColor()
   * '#ffffff'
   * @returns {{string}} The hero background color string, or an empty string if none is found.
   **/
  getHeroBackgroundColor(): string {
    const heroElement = this.getElementByIndex(1);
    if (!heroElement) return '';

    const layout = heroElement.content?.layout;
    // Check if layout is an object with background_color
    if (typeof layout === 'object' && layout !== null && 'background_color' in layout) {
      return (layout as any).background_color || '';
    }

    // Fall back to content.backgroundColor
    return heroElement.content?.backgroundColor || '';
  }

  getWelcomeLayout(): '1' | '2' | '3' {
    const welcomeElement = this.getElementByIndex(2);
    return (welcomeElement?.content?.layout as '1' | '2' | '3') || '1';
  }

  /**
   * Check if an element is an info section element
   */
  isInfoSectionElement(element: WebsiteElement): boolean {
    if (!element) return false;
    // Match elements with type 'section', or IDs that start with 'welcome' or 'info'
    return (
      element.type === 'section' ||
      element.id === 'welcome' ||
      !!(element.id && element.id.startsWith('welcome_')) ||
      !!(element.id && element.id.startsWith('info_'))
    );
  }

  /**
   * Get all info section elements (including numbered variants like info_section_1, info_section_2)
   * Note: This method is kept for backward compatibility but elements should be rendered in order
   */
  getInfoSectionElements(): WebsiteElement[] {
    return this.elements.filter(el => {
      return this.isInfoSectionElement(el);
    });
  }

  /**
   * Get layout for a specific info section element
   */
  getInfoSectionLayout(
    element: WebsiteElement
  ): 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' {
    if (!element) return 'layout_1';

    const layout = element.content?.layout;
    if (!layout) return 'layout_1';

    // Handle API structure: layout is an object with type property
    if (typeof layout === 'object' && layout !== null && 'type' in layout) {
      const layoutType = (layout as any).type;
      if (typeof layoutType === 'string' && layoutType.startsWith('layout_')) {
        if (
          layoutType === 'layout_1' ||
          layoutType === 'layout_2' ||
          layoutType === 'layout_3' ||
          layoutType === 'layout_4' ||
          layoutType === 'layout_5'
        ) {
          return layoutType as 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5';
        }
      }
      // If type is just a number, convert to layout_X format
      if (typeof layoutType === 'string' && /^[1-5]$/.test(layoutType)) {
        return `layout_${layoutType}` as
          'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5';
      }
    }

    // Handle old format: layout is a string like '1', '2', etc.
    if (typeof layout === 'string') {
      if (layout.startsWith('layout_')) {
        if (
          layout === 'layout_1' ||
          layout === 'layout_2' ||
          layout === 'layout_3' ||
          layout === 'layout_4' ||
          layout === 'layout_5'
        ) {
          return layout as 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5';
        }
      }
      if (/^[1-5]$/.test(layout)) {
        return `layout_${layout}` as 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5';
      }
    }

    return 'layout_1';
  }

  /**
   * Check if an info section element should be visible
   */
  isInfoSectionVisible(element: WebsiteElement): boolean {
    // First check if element exists and has content
    if (!element || !element.content) {
      return false; // Hide if element doesn't exist or has no content
    }

    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }

    // Check visibility by element ID
    if (this.sectionVisibility[element.id] !== undefined) {
      return this.sectionVisibility[element.id] !== false;
    }

    // Check visibility by original key if available
    const originalKey = (element as any).originalKey;
    if (originalKey && this.sectionVisibility[originalKey] !== undefined) {
      return this.sectionVisibility[originalKey] !== false;
    }

    // Default to visible if element exists
    return true;
  }

  /**
   * Check whether the contact element at index 6 contains a non-empty businessHours array (also logs debug info).
   * @example
   * asBusinessHours()
   * true
   * @returns {boolean} True if a businessHours array exists and has at least one entry; otherwise false.
   **/
  hasBusinessHours(): boolean {
    const contactElement = this.getElementByIndex(6);
    const hasHours = !!(
      contactElement?.content?.businessHours &&
      Array.isArray(contactElement.content.businessHours) &&
      contactElement.content.businessHours.length > 0
    );
    this.logger.debug('hasBusinessHours check:', {
      hasContactElement: !!contactElement,
      hasBusinessHours: !!contactElement?.content?.businessHours,
      isArray: Array.isArray(contactElement?.content?.businessHours),
      length: contactElement?.content?.businessHours?.length || 0,
      result: hasHours,
      businessHours: contactElement?.content?.businessHours,
    });
    return hasHours;
  }

  /**
   * Return a sanitized SafeResourceUrl for the embedded map iframe or null if no valid URL is available.
   * @example
   * etMapEmbedUrl()
   * SafeResourceUrl
   * @returns {SafeResourceUrl|null} Sanitized SafeResourceUrl for embedding the map, or null if no valid URL.
   **/
  getMapEmbedUrl(): SafeResourceUrl | null {
    const contactElement = this.getElementByIndex(6);
    const mapEmbedUrl = contactElement?.content?.mapEmbedUrl;

    if (!mapEmbedUrl || typeof mapEmbedUrl !== 'string') {
      // Clear cache if no URL
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      return null;
    }

    // Extract URL from iframe HTML if needed
    const trimmedUrl = mapEmbedUrl.trim();

    // Check if we have a cached value for this URL
    if (this.cachedMapEmbedUrl && this.cachedMapEmbedUrlSource === trimmedUrl) {
      return this.cachedMapEmbedUrl;
    }

    // Process the URL
    let processedUrl: string | null = trimmedUrl;

    // If it contains iframe HTML, extract the src URL
    if (trimmedUrl.includes('<iframe') || trimmedUrl.includes('&lt;iframe')) {
      try {
        // Try regex extraction first
        const srcMatch = trimmedUrl.match(/src\s*=\s*"([^"]+)"|src\s*=\s*'([^']+)'/i);
        if (srcMatch) {
          const extractedUrl = (srcMatch[1] || srcMatch[2] || '').trim();
          if (extractedUrl) {
            // Decode HTML entities
            processedUrl = extractedUrl
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&');
          }
        }

        // Try DOMParser as fallback if regex didn't work
        if (!processedUrl || processedUrl === trimmedUrl) {
          const wrappedHtml = `<div>${trimmedUrl}</div>`;
          const parser = new DOMParser();
          const doc = parser.parseFromString(wrappedHtml, 'text/html');
          const iframe = doc.querySelector('iframe');
          if (iframe) {
            processedUrl = iframe.getAttribute('src');
          }
        }
      } catch (error) {
        this.logger.warn('Error extracting URL from iframe HTML:', error);
        this.cachedMapEmbedUrl = null;
        this.cachedMapEmbedUrlSource = null;
        return null;
      }
    }

    // If we couldn't extract a valid URL, clear cache and return null
    if (!processedUrl) {
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      return null;
    }

    // Only allow http(s) URLs — reject javascript:, data:, or any other scheme
    if (!processedUrl.startsWith('https://') && !processedUrl.startsWith('http://')) {
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      return null;
    }

    // Cache the sanitized URL
    this.cachedMapEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(processedUrl);
    this.cachedMapEmbedUrlSource = trimmedUrl;

    return this.cachedMapEmbedUrl;
  }

  /**
   * Get footer quick links from footer element or active pages
   * Filters out pages where visible === false (active === false)
   */
  getFooterQuickLinks(): { label: string; link: string }[] {
    // First, try to get from footer element content
    const footerElement = this.getElementByIndex(7);
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Get footer social links from footer element
   */
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getElementByIndex(7);
    const footerContent = footerElement?.content as any;
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      // Map to ensure all required properties are present
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }
    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getElementByIndex(7);
    const footerContent = footerElement?.content as any;
    return footerContent?.socialLinksTitle || 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getElementByIndex(7);
    const footerContent = footerElement?.content as any;
    return footerContent?.quickLinksTitle || 'Quick links';
  }

  /**
   * Get footer copyright text from footer element
   */
  getFooterCopyright(): string {
    const footerElement = this.getElementByIndex(7);
    const footerContent = footerElement?.content as any;
    if (footerContent?.copyright) {
      return footerContent.copyright;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Get footer logo from footer element content
   */
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getElementByIndex(7);
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }

    return { text: 'way' };
  }

  /**
   * Get footer background color from footer element
   */
  getFooterBackgroundColor(): string {
    const footerElement = this.getElementByIndex(7);
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#1B1B1B';
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  /**
   * Convert a button link to a page type for navigation
   * Handles links like "/express-washes", "express-washes", "/", etc.
   */
  convertLinkToPageType(link: string | undefined): string {
    if (!link) return 'home';

    // Remove leading/trailing slashes and normalize
    const normalizedLink = link.replace(/^\/+|\/+$/g, '').toLowerCase();

    if (!normalizedLink || normalizedLink === 'home' || normalizedLink === '') return 'home';
    if (normalizedLink.includes('express-wash') || normalizedLink === 'express-washes')
      return 'express-wash-packages';
    if (
      normalizedLink.includes('express-detail') ||
      normalizedLink.includes('express-auto') ||
      normalizedLink === 'express-details'
    )
      return 'express-auto-details';
    if (normalizedLink.includes('fleet')) return 'fleet-management';
    if (normalizedLink.includes('coupon')) return 'coupons';

    // Try to match with page names
    const matchingPage = this.pages.find(p => {
      const pageName = (p.name || '').toLowerCase();
      const pagePath = (p.path || '').replace(/^\/+|\/+$/g, '').toLowerCase();
      return pageName === normalizedLink || pagePath === normalizedLink;
    });

    if (matchingPage) {
      const normalizedName = matchingPage.name.toLowerCase().trim();
      if (normalizedName === 'home') return 'home';
      if (normalizedName.includes('express wash') || normalizedName.includes('express_wash'))
        return 'express-wash-packages';
      if (
        normalizedName.includes('express detail') ||
        normalizedName.includes('express_detail') ||
        normalizedName.includes('express auto')
      )
        return 'express-auto-details';
      if (normalizedName.includes('fleet')) return 'fleet-management';
      if (normalizedName.includes('coupon')) return 'coupons';
    }

    return 'home';
  }

  /**
   * Handle button click - navigate to the page specified by buttonLink
   */
  onButtonClick(buttonLink: string | undefined, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    const pageType = this.convertLinkToPageType(buttonLink);
    this.navigationClick.emit(pageType);
  }

  /**
   * Handle booking button click - navigate to the page specified by buttonLink
   */
  onBookingButtonClick(element: WebsiteElement | undefined, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const buttonLink = (element?.content as any)?.buttonLink;
    const pageType = this.convertLinkToPageType(buttonLink);
    this.navigationClick.emit(pageType);
  }

  /**
   * Safely get title as string (handles both string and object formats)
   */
  getTitleSafe(element: WebsiteElement | undefined): string {
    if (!element?.content?.title) return '';
    const title = element.content.title;
    if (typeof title === 'string') return title;
    if (typeof title === 'object' && title !== null) {
      return (title as any).label || String(title);
    }
    return String(title);
  }

  /**
   * Safely get description as string (handles both string and object formats)
   */
  getDescriptionSafe(element: WebsiteElement | undefined): string {
    if (!element?.content?.description) return '';
    const description = element.content.description;
    if (typeof description === 'string') return description;
    if (typeof description === 'object' && description !== null) {
      return (description as any).text || (description as any).value || String(description);
    }
    return String(description);
  }

  /**
   * Get hero layout data for layout components
   */
  getHeroLayoutData() {
    const heroElement = this.getElementByIndex(1);
    if (!heroElement) return null;

    // Get background color from layout object if available
    const backgroundColor = this.getHeroBackgroundColor();

    return {
      title: heroElement.content?.title,
      subtitle: heroElement.content?.subtitle,
      description: heroElement.content?.description,
      buttonText: heroElement.content?.buttonText,
      buttonLink: heroElement.content?.buttonLink,
      image: heroElement.content?.image,
      imageUrl: heroElement.content?.imageUrl,
      backgroundColor: backgroundColor,
      titleStyle: heroElement.content?.titleStyle,
    };
  }

  /**
   * Get info layout data for layout components
   */
  getInfoLayoutData(
    element: WebsiteElement
  ): InfoLayout1Data | InfoLayout2Data | InfoLayout3Data | InfoLayout4Data | InfoLayout5Data {
    return {
      title: element.content?.title,
      description: element.content?.description,
      subtitle: element.content?.subtitle,
      buttonText: element.content?.buttonText,
      buttonLink: element.content?.buttonLink,
      image: element.content?.image,
      imageUrl: element.content?.imageUrl,
      backgroundColor: element.content?.backgroundColor,
    };
  }

  /**
   * Get booking layout type
   */
  getBookingLayout(element: WebsiteElement): 'layout_1' | 'layout_2' {
    if (!element) return 'layout_2';

    const layout = element.content?.layout;
    if (!layout) return 'layout_2';

    // Handle API structure: layout is an object with type property
    if (typeof layout === 'object' && layout !== null && 'type' in layout) {
      const layoutType = (layout as any).type;
      if (typeof layoutType === 'string' && layoutType.startsWith('layout_')) {
        return layoutType === 'layout_2' ? 'layout_2' : 'layout_1';
      }
      if (typeof layoutType === 'string' && layoutType === '2') {
        return 'layout_2';
      }
      if (typeof layoutType === 'string' && layoutType === '1') {
        return 'layout_1';
      }
    }

    // Handle old format: layout is a string like '1', '2', etc.
    if (typeof layout === 'string') {
      if (layout === 'layout_2' || layout === '2') {
        return 'layout_2';
      }
      if (layout === 'layout_1' || layout === '1') {
        return 'layout_1';
      }
    }

    return 'layout_2';
  }

  /**
   * Get booking layout data for layout component
   */
  getBookingLayoutData(element: WebsiteElement): BookingLayout1Data | BookingLayout2Data {
    const title =
      typeof element.content?.title === 'object' && element.content?.title !== null
        ? (element.content.title as any).label || element.content.title
        : element.content?.title;

    // Extract button text and colors from button object
    // Check both structures: content.button.backgroundColor (editor format) and content.buttonBackgroundColor (website preview format)
    const content = element.content as any;
    const buttonText =
      content?.button?.text || content?.buttonText || content?.button?.label || 'Book Appointment';
    const buttonBackgroundColor =
      content?.button?.backgroundColor ||
      content?.button?.style?.background ||
      content?.buttonBackgroundColor ||
      null;
    const buttonTextColor =
      content?.button?.textColor ||
      content?.button?.style?.color ||
      content?.buttonTextColor ||
      null;

    return {
      title: title || '',
      description: element.content?.description || element.content?.subtitle || '',
      buttonText: buttonText,
      buttonBackgroundColor: buttonBackgroundColor,
      buttonTextColor: buttonTextColor,
      backgroundColor: element.content?.backgroundColor,
    };
  }

  /**
   * Get booking form data (local storage for form values)
   */
  bookingFormData = new Map<string, Record<string, any>>();

  getBookingFormData(element: WebsiteElement): Record<string, any> {
    const key = element.id || 'booking';
    if (!this.bookingFormData.has(key)) {
      this.bookingFormData.set(key, {});
    }
    return this.bookingFormData.get(key)!;
  }

  /**
   * Handle booking form submit from layout component
   */
  onBookingLayoutSubmit(
    element: WebsiteElement,
    formData: Record<string, any>,
    event: Event
  ): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Handle form submission logic here
    this.logger.debug('Booking form submitted:', formData);
  }

  /**
   * Handle field change from layout component
   */
  onBookingLayoutFieldChange(element: WebsiteElement, field: any, value: any): void {
    const formData = this.getBookingFormData(element);
    const fieldKey = field?.name || field?.id;
    if (fieldKey) {
      formData[fieldKey] = value;
    }
  }

  /**
   * Handle date/time change from layout component
   */
  onBookingLayoutDateTimeChange(element: WebsiteElement, field: any, dateTime: Date | null): void {
    const formData = this.getBookingFormData(element);
    const fieldKey = field?.name || field?.id;

    if (!dateTime) {
      formData[fieldKey] = '';
      return;
    }

    // Set date value (YYYY-MM-DD format)
    const dateStr = dateTime.toISOString().split('T')[0];
    formData[fieldKey] = dateStr;

    // Set time value (HH:MM format) if needed
    const timeValue = `${dateTime.getHours().toString().padStart(2, '0')}:${dateTime.getMinutes().toString().padStart(2, '0')}`;
    // Look for a time field
    const fields = element.content?.formFields || [];
    const timeField = fields.find((f: any) => f.type === 'time' && f.name !== fieldKey);
    if (timeField) {
      formData[timeField.name || timeField.id] = timeValue;
    }
  }

  /**
   * Get title styles from element content
   */
  getTitleStyles(element: WebsiteElement | undefined): any {
    if (!element?.content?.titleStyle) {
      return {};
    }
    const style = element.content.titleStyle;
    if (typeof style === 'string') {
      return { color: style };
    }
    return {};
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#FFFFFF';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#D3D5D9';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Plus Jakarta Sans';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Get contact layout data for layout component
   */
  getContactLayoutData(element: WebsiteElement): ContactLayout2Data {
    // Extract phone - handle both string and object formats
    const phoneValue = element.content?.phone || element.content?.contactInfo?.phone;
    const phone =
      typeof phoneValue === 'string'
        ? phoneValue
        : phoneValue && typeof phoneValue === 'object'
          ? (phoneValue as any).value ||
            (phoneValue as any).phone ||
            (phoneValue as any).label ||
            ''
          : '';

    // Extract email - handle both string and object formats
    const emailValue = element.content?.email || element.content?.contactInfo?.email;
    const email =
      typeof emailValue === 'string'
        ? emailValue
        : emailValue && typeof emailValue === 'object'
          ? (emailValue as any).value ||
            (emailValue as any).email ||
            (emailValue as any).label ||
            ''
          : '';

    return {
      title: this.getTitleSafe(element),
      subtitle: element.content?.description || element.content?.subtitle || '',
      address:
        element.content?.address ||
        (element.content?.contactInfo?.address
          ? typeof element.content.contactInfo.address === 'string'
            ? element.content.contactInfo.address
            : `${element.content.contactInfo.address?.street || ''} ${element.content.contactInfo.address?.city || ''}, ${element.content.contactInfo.address?.state || ''} ${element.content.contactInfo.address?.zipCode || ''}`.trim()
          : ''),
      phone: phone,
      email: email,
      mapEmbedUrl: element.content?.mapEmbedUrl || '',
      mapImage: element.content?.map?.image || '',
      mapAlt: element.content?.map?.alt || 'Map location',
      businessHours: element.content?.businessHours || [],
      backgroundColor: element.content?.backgroundColor,
    };
  }

  /**
   * Get testimonials array from testimonials element
   */
  getTestimonials(): any[] {
    const testimonialsElement = this.getElementByIndex(3);
    return testimonialsElement?.content?.testimonials || [];
  }

  /**
   * Get total number of testimonial slides (showing 3 per slide on desktop, 1 on mobile/tablet)
   */
  getTotalTestimonialSlides(): number {
    const testimonials = this.getTestimonials();
    if (testimonials.length === 0) return 0;

    if (this.deviceType === 'desktop') {
      // Desktop: show 3 testimonials per slide
      return Math.ceil(testimonials.length / 3);
    } else {
      // Mobile/Tablet: show 1 testimonial per slide
      return testimonials.length;
    }
  }

  /**
   * Get testimonials for current slide
   */
  getTestimonialsForSlide(slideIndex: number): any[] {
    const testimonials = this.getTestimonials();
    if (testimonials.length === 0) return [];

    if (this.deviceType === 'desktop') {
      // Desktop: 3 testimonials per slide
      const startIndex = slideIndex * 3;
      return testimonials.slice(startIndex, startIndex + 3);
    } else {
      // Mobile/Tablet: 1 testimonial per slide
      return [testimonials[slideIndex]].filter(Boolean);
    }
  }

  /**
   * Get pagination dots array
   */
  getTestimonialPaginationDots(): number[] {
    const total = this.getTotalTestimonialSlides();
    return Array.from({ length: total }, (_, i) => i);
  }

  /**
   * Navigate to specific slide
   */
  goToTestimonialSlide(index: number): void {
    const total = this.getTotalTestimonialSlides();
    if (index >= 0 && index < total) {
      this.currentTestimonialSlide = index;
      this.resetAutoSlide();
    }
  }

  /**
   * Navigate to next slide
   */
  nextTestimonialSlide(): void {
    const total = this.getTotalTestimonialSlides();
    if (this.currentTestimonialSlide < total - 1) {
      this.currentTestimonialSlide++;
    } else {
      // Loop back to first slide
      this.currentTestimonialSlide = 0;
    }
    this.resetAutoSlide();
  }

  /**
   * Navigate to previous slide
   */
  prevTestimonialSlide(): void {
    const total = this.getTotalTestimonialSlides();
    if (this.currentTestimonialSlide > 0) {
      this.currentTestimonialSlide--;
    } else {
      // Loop to last slide
      this.currentTestimonialSlide = total - 1;
    }
    this.resetAutoSlide();
  }

  /**
   * Start auto-slide
   */
  startAutoSlide(): void {
    this.stopAutoSlide();
    this.autoSlideInterval = setInterval(() => {
      this.nextTestimonialSlide();
    }, this.AUTO_SLIDE_INTERVAL);
  }

  /**
   * Stop auto-slide
   */
  stopAutoSlide(): void {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
      this.autoSlideInterval = null;
    }
  }

  /**
   * Reset auto-slide (restart timer)
   */
  resetAutoSlide(): void {
    this.startAutoSlide();
  }

  /**
   * Handle touch start for swipe
   */
  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  /**
   * Handle touch end for swipe
   */
  onTouchEnd(event: TouchEvent): void {
    this.touchEndX = event.changedTouches[0].screenX;
    this.handleSwipe();
  }

  /**
   * Handle swipe gesture
   */
  private handleSwipe(): void {
    const swipeThreshold = 50; // Minimum distance for swipe
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swipe left - next slide
        this.nextTestimonialSlide();
      } else {
        // Swipe right - previous slide
        this.prevTestimonialSlide();
      }
    }
  }

  /**
   * Initialize carousel on component init
   */
  ngOnInit(): void {
    // Start auto-slide when component initializes if there are testimonials
    const testimonials = this.getTestimonials();
    if (testimonials.length > 0 && this.getTotalTestimonialSlides() > 1) {
      this.startAutoSlide();
    }
  }

  /**
   * Cleanup on component destroy
   */
  ngOnDestroy(): void {
    this.stopAutoSlide();
  }

  /**
   * Track by function for slides
   */
  trackByIndex(index: number): number {
    return index;
  }

  /**
   * Track by function for testimonials
   */
  trackByTestimonialId(index: number, testimonial: any): string {
    return testimonial?.id || index.toString();
  }
}
